// Panel de la página Configuración. Extraído de pages/Configuracion.tsx
// (modularización 2026-06-11) sin cambios de comportamiento.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  api,
  type ClienteConContactos,
  type Contacto,
} from "@/lib/api";
import AsesorPicker from "@/components/asesor/AsesorPicker";
import CasosDelContactoModal from "@/components/CasosDelContactoModal";
import EditarContactoModal from "@/components/EditarContactoModal";
import EditarEmpresaModal from "@/components/EditarEmpresaModal";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";
import { useAuth, canEdit } from "@/lib/auth";

// ============================================================================
// CONTACTOS (agrupados por empresa)
// ============================================================================

/** Pestana "Contactos" — vista global de todos los clientes con sus
 * contactos. Permite editar, marcar principal, agregar y borrar. La info
 * vive en un solo lugar (tabla `contactos` con FK al cliente); cualquier
 * caso que apunte a un contacto refleja los cambios al guardar.
 *
 * @param focusClienteId  Si se pasa, después de cargar: hace scroll hasta
 *   esa tarjeta, la resalta brevemente y abre el AsesorPicker en modo edición
 *   (deep-link desde el detalle del caso).
 */
export default function ContactosPanel({ focusClienteId }: { focusClienteId?: number }) {
  const toast = useToast();
  const { user } = useAuth();
  const [data, setData] = useState<ClienteConContactos[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Por cliente, controlamos si la seccion "Archivados" esta abierta.
  const [archivadosAbiertos, setArchivadosAbiertos] = useState<Set<number>>(
    new Set(),
  );
  const [editing, setEditing] = useState<{
    cliente: ClienteConContactos;
    contacto: Contacto | null; // null = crear nuevo en este cliente
  } | null>(null);
  const [verCasos, setVerCasos] = useState<{
    cliente: ClienteConContactos;
    contacto: Contacto;
  } | null>(null);
  const [editarEmpresa, setEditarEmpresa] = useState<ClienteConContactos | null>(null);
  const [confirmArchivar, setConfirmArchivar] = useState<{
    cliente_id: number;
    contacto: Contacto;
  } | null>(null);

  // Tarjeta resaltada por deep-link (la que corresponde a focusClienteId)
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  // autoOpen del picker = de UNA sola vez. Se inicializa con focusClienteId y
  // se limpia tras consumirlo, para que un refresh posterior (al guardar) NO
  // vuelva a abrir el dropdown solo.
  const [autoOpenId, setAutoOpenId] = useState<number | null>(focusClienteId ?? null);
  const deepLinkConsumed = useRef(false);

  // Refs para las tarjetas de cliente (indexadas por cliente.id)
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<ClienteConContactos[]>(
        "/api/clientes/con-contactos",
      );
      setData(list);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar contactos", msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  // Deep-link: scroll + highlight + autoOpen cuando se carga la lista (UNA vez)
  useEffect(() => {
    if (deepLinkConsumed.current) return;
    if (!focusClienteId || loading || data.length === 0) return;
    const card = cardRefs.current.get(focusClienteId);
    if (!card) return;
    deepLinkConsumed.current = true;

    card.scrollIntoView({ behavior: "smooth", block: "center" });

    // Resaltar 2 segundos con ring
    setHighlightedId(focusClienteId);
    const t = setTimeout(() => setHighlightedId(null), 2000);
    // El picker ya montó abierto; limpiamos el autoOpen para que NO se reabra
    // tras el refresh que dispara guardar el asesor.
    setAutoOpenId(null);
    return () => clearTimeout(t);
  }, [focusClienteId, loading, data]);

  // Filtrado por nombre de cliente o nombre/email/telefono de contacto.
  // Si el cliente no matchea pero un contacto si, se mantiene el cliente
  // pero solo se listan los contactos que coinciden — facilita encontrar
  // "ese contacto que no recuerdo en que empresa esta".
  const filtrado = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data;
    return data
      .map((cli) => {
        if (cli.nombre.toLowerCase().includes(needle)) return cli;
        const contactosMatch = cli.contactos.filter(
          (c) =>
            c.nombre.toLowerCase().includes(needle) ||
            (c.email ?? "").toLowerCase().includes(needle) ||
            (c.telefono ?? "").toLowerCase().includes(needle),
        );
        if (contactosMatch.length === 0) return null;
        return { ...cli, contactos: contactosMatch };
      })
      .filter((c): c is ClienteConContactos => c !== null);
  }, [data, q]);

  async function archivar(cliente_id: number, c: Contacto) {
    try {
      await api.patch<Contacto>(
        `/api/clientes/${cliente_id}/contactos/${c.id}`,
        { activo: false },
      );
      toast.success("Contacto archivado", c.nombre);
      setConfirmArchivar(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude archivar", msg);
    }
  }

  async function reactivar(cliente_id: number, c: Contacto) {
    try {
      await api.patch<Contacto>(
        `/api/clientes/${cliente_id}/contactos/${c.id}`,
        { activo: true },
      );
      toast.success("Contacto reactivado", c.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude reactivar", msg);
    }
  }

  function toggleArchivados(cliente_id: number) {
    setArchivadosAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(cliente_id)) next.delete(cliente_id);
      else next.add(cliente_id);
      return next;
    });
  }

  async function asignarAsesor(clienteId: number, asesorId: number | null) {
    // Por si el picker venía abierto por deep-link: que el refresh no lo reabra.
    setAutoOpenId(null);
    try {
      await api.patch(`/api/clientes/${clienteId}`, { asesor_comercial_id: asesorId });
      toast.success("Asesor actualizado");
      // Actualizar en local sin re-fetch completo para evitar flicker
      setData((prev) =>
        prev.map((cli) => {
          if (cli.id !== clienteId) return cli;
          return { ...cli, asesor_comercial_id: asesorId, asesor_comercial_nombre: cli.asesor_comercial_nombre };
        }),
      );
      // Re-fetch para obtener el nombre actualizado del asesor
      void refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude asignar el asesor", msg);
    }
  }

  const totalActivos = data.reduce(
    (acc, c) => acc + c.contactos.filter((x) => x.activo).length,
    0,
  );
  const totalArchivados = data.reduce(
    (acc, c) => acc + c.contactos.filter((x) => !x.activo).length,
    0,
  );

  return (
    <>
      <div className="card">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
          <div className="flex-1">
            <p className="text-xs text-zinc-500">
              Los contactos viven a nivel de cliente y se reusan en todos sus
              casos. Editar aquí actualiza la info en cada caso que lo
              referencia. <strong>Archivar</strong> oculta al contacto del
              selector para casos nuevos, pero los casos viejos lo siguen
              mostrando — el historial queda intacto.
            </p>
          </div>
          <span className="text-xs text-zinc-500 whitespace-nowrap mt-1">
            {totalActivos} activo{totalActivos === 1 ? "" : "s"}
            {totalArchivados > 0 && ` · ${totalArchivados} archivado${totalArchivados === 1 ? "" : "s"}`}
            {" · "}
            {data.length} empresa{data.length === 1 ? "" : "s"}
          </span>
        </div>

        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por empresa, nombre, email o teléfono…"
          className="input w-full"
        />
      </div>

      {loading ? (
        <div className="card">
          <SkeletonRows rows={5} cols={3} />
        </div>
      ) : filtrado.length === 0 ? (
        <div className="card text-sm text-zinc-500">
          {q.trim()
            ? "Ningún cliente o contacto coincide."
            : "Aún no hay clientes. Se crean al crear un caso."}
        </div>
      ) : (
        filtrado.map((cli) => {
          const activos = cli.contactos.filter((c) => c.activo);
          const archivados = cli.contactos.filter((c) => !c.activo);
          const showArchivados = archivadosAbiertos.has(cli.id);
          const isHighlighted = highlightedId === cli.id;
          // El picker debe abrirse automáticamente si llegamos por deep-link a este cliente
          const autoOpenAsesor = autoOpenId === cli.id;
          return (
            <div
              key={cli.id}
              ref={(el) => {
                if (el) cardRefs.current.set(cli.id, el);
                else cardRefs.current.delete(cli.id);
              }}
              className={`card transition-all duration-300 ${
                isHighlighted ? "ring-2 ring-accent-500/50" : ""
              }`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-1.5 min-w-0 mb-1">
                    <span className="truncate">{cli.nombre}</span>
                    <button
                      onClick={() => setEditarEmpresa(cli)}
                      className="text-zinc-500 hover:text-zinc-200 transition text-xs"
                      aria-label={`Renombrar ${cli.nombre}`}
                    >
                      ✎
                    </button>
                    <span className="text-xs text-zinc-500 font-normal ml-1">
                      {activos.length} activo{activos.length === 1 ? "" : "s"}
                      {archivados.length > 0 &&
                        ` · ${archivados.length} archivado${archivados.length === 1 ? "" : "s"}`}
                    </span>
                  </h2>
                  {canEdit(user) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-zinc-500 whitespace-nowrap">Asesor:</span>
                      <AsesorPicker
                        value={cli.asesor_comercial_id}
                        onChange={(id) => void asignarAsesor(cli.id, id)}
                        allowClear
                        autoOpen={autoOpenAsesor}
                      />
                    </div>
                  )}
                  {!canEdit(user) && cli.asesor_comercial_nombre && (
                    <p className="text-xs text-zinc-500">
                      Asesor: <span className="text-zinc-300">{cli.asesor_comercial_nombre}</span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setEditing({ cliente: cli, contacto: null })}
                  className="btn-ghost text-xs w-full sm:w-auto sm:mt-0.5"
                >
                  + Nuevo contacto
                </button>
              </div>

              {activos.length === 0 && archivados.length === 0 ? (
                <p className="text-xs text-zinc-500 py-2">
                  Sin contactos registrados. Agrega uno con el botón de arriba.
                </p>
              ) : (
                <>
                  {activos.length === 0 ? (
                    <p className="text-xs text-zinc-500 py-2">
                      Sin contactos activos. Reactiva uno desde la sección
                      archivados o registra uno nuevo.
                    </p>
                  ) : (
                    <ul className="divide-y divide-ink-700">
                      {activos.map((c) => (
                        <ContactoRow
                          key={c.id}
                          c={c}
                          onEdit={() => setEditing({ cliente: cli, contacto: c })}
                          onVerCasos={() => setVerCasos({ cliente: cli, contacto: c })}
                          rightAction={
                            <button
                              onClick={() =>
                                setConfirmArchivar({
                                  cliente_id: cli.id,
                                  contacto: c,
                                })
                              }
                              className="btn-ghost text-xs"
                            >
                              Archivar
                            </button>
                          }
                        />
                      ))}
                    </ul>
                  )}

                  {archivados.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-ink-700">
                      <button
                        onClick={() => toggleArchivados(cli.id)}
                        className="text-xs text-zinc-400 hover:text-zinc-200 transition flex items-center gap-1"
                      >
                        <span>{showArchivados ? "▾" : "▸"}</span>
                        Archivados ({archivados.length})
                      </button>
                      {showArchivados && (
                        <ul className="divide-y divide-ink-700 mt-2 opacity-60">
                          {archivados.map((c) => (
                            <ContactoRow
                              key={c.id}
                              c={c}
                              onEdit={() =>
                                setEditing({ cliente: cli, contacto: c })
                              }
                              onVerCasos={() =>
                                setVerCasos({ cliente: cli, contacto: c })
                              }
                              rightAction={
                                <button
                                  onClick={() => void reactivar(cli.id, c)}
                                  className="btn-ghost text-xs text-accent-400 hover:text-accent-300"
                                >
                                  Reactivar
                                </button>
                              }
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })
      )}

      {editing && (
        <EditarContactoModal
          open={true}
          onClose={() => setEditing(null)}
          clienteId={editing.cliente.id}
          clienteNombre={editing.cliente.nombre}
          contacto={editing.contacto}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}

      {verCasos && (
        <CasosDelContactoModal
          open={true}
          onClose={() => setVerCasos(null)}
          contacto={verCasos.contacto}
          clienteNombre={verCasos.cliente.nombre}
        />
      )}

      {editarEmpresa && (
        <EditarEmpresaModal
          open={true}
          onClose={() => setEditarEmpresa(null)}
          cliente={{ id: editarEmpresa.id, nombre: editarEmpresa.nombre }}
          onSaved={() => {
            setEditarEmpresa(null);
            void refresh();
          }}
        />
      )}

      {confirmArchivar && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmArchivar(null)}
          title={`Archivar a "${confirmArchivar.contacto.nombre}"`}
          description={
            "Dejará de aparecer en el selector de contactos al crear casos " +
            "o registrar llamadas nuevas. Los casos viejos que lo apuntan " +
            "no cambian — el historial queda intacto. Puedes reactivarlo " +
            "en cualquier momento."
          }
          confirmLabel="Archivar"
          onConfirm={() =>
            archivar(confirmArchivar.cliente_id, confirmArchivar.contacto)
          }
        />
      )}
    </>
  );
}

/** Fila de un contacto dentro del panel — extraida porque la usamos en
 * dos lugares (activos y archivados) con la misma estructura visual y
 * solo el boton de la derecha cambia.
 */
function ContactoRow({
  c,
  onEdit,
  onVerCasos,
  rightAction,
}: {
  c: Contacto;
  onEdit: () => void;
  onVerCasos: () => void;
  rightAction: React.ReactNode;
}) {
  return (
    <li className="py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-200 truncate">{c.nombre}</span>
          {c.principal && c.activo && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-accent-500/30 bg-accent-500/10 text-accent-300">
              principal
            </span>
          )}
          {!c.activo && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-zinc-600 bg-zinc-700/30 text-zinc-400">
              archivado
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-0.5 truncate">
          {[c.email, c.telefono].filter(Boolean).join(" · ") || "—"}
        </p>
      </div>
      <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
        <button onClick={onVerCasos} className="btn-ghost text-xs">
          Casos
        </button>
        <button onClick={onEdit} className="btn-ghost text-xs">
          Editar
        </button>
        {rightAction}
      </div>
    </li>
  );
}
